<template>
  <section class="items-feed-assign-table">
    <f-data-table id="item-feed-table" :data-uri="data_uri"
                  :selectable-rows="true"
                  :columns="columns"
                  :static-params="static_params"
                  :heading="getItemLabel(true) + 's'"
                  icon="la la-file-audio-o"
                  :expanded="expanded"
                  :server-side="true">
      <template #_actions="props">
        <div class="kt-portlet__head-toolbar">
          <div class="kt-portlet__head-wrapper">
            <button type="button"
                    class="btn btn-ss btn-icon-sm"
                    @click="props.reloadData()">
              <i class="flaticon2-refresh" />Reload Data
            </button>
          </div>
        </div>
      </template>
      <template #_filters="filters_global">
        <f-data-table-filters target="item-feed-table"
                              :expanded="true"
                              :form-columns="2">
          <template #host_feed>
            <f-data-table-filter>
              <label for="ms-owned">Filter {{ getItemLabel() }}s included in / excluded from <strong>{{ feed.name }}</strong>:</label>
              <multiselect id="ms-owned"
                           v-model="ms_owned.val"
                           :options="ms_owned.options"
                           track-by="param"
                           label="display"
                           @input="(event) => toggleOwned(event, filters_global.reloadData)" />
            </f-data-table-filter>
          </template>
          <template #name="props">
            <f-data-table-filter field="name"
                                 display="Name:"
                                 placeholder="Search name..."
                                 :scope="props" />
          </template>
          <template #desc="props">
            <f-data-table-filter field="description"
                                 display="Description:"
                                 placeholder="Search description..."
                                 :scope="props" />
          </template>
          <template #published="props">
            <f-data-table-filter>
              <label for="published-status-select">Published:</label>
              <multiselect v-model="published_multi_select_val"
                           name="published-status-select"
                           :options="[ { display: `Yes`, val: 1 }, { display: `No`, val: 0 } ]"
                           label="display"
                           track-by="val"
                           placeholder="Select a status..."
                           @input="(selected) => {
                             props.filterChange({
                               client: {
                                 op: (val) => {
                                   if (!selected) {
                                     return true;
                                   } else {
                                     return selected.val === val;
                                   }
                                 },
                                 field: `published`
                               },
                               server: {
                                 type: `scalar`,
                                 op: `eq`,
                                 field: `published`,
                                 value: selected.val,
                               }
                             })
                           }" />
            </f-data-table-filter>
          </template>
          <template #tags="props">
            <f-data-table-filter>
              <label for="tags-search">Search tags:</label>
              <multiselect v-model="tags_input_val"
                           name="tags-search"
                           :options="tags_ac_list"
                           track-by="uuid"
                           label="name"
                           :multiple="true"
                           placeholder="Search tags..."
                           @input="(event) => {
                             props.filterChange({
                               server: {
                                 type: `param`,
                                 field: `tags`,
                                 value: tags_input_val.map(tag => tag.name).join(`,`)
                               }
                             });
                           }" />
            </f-data-table-filter>
          </template>
        </f-data-table-filters>
      </template>
      <template #_row_controls="props">
        <button v-if="ms_owned.val.param === `excluded_feed_uuid`" class="btn btn-ss" @click="props.do({cb:assignToFeed, reload_data: true})">
          <i class="flaticon2-plus" />Assign to feed
        </button>
        <button v-else class="btn btn-ss-cancel" @click="props.do({cb:unassignFromFeed, reload_data: true})">
          <i class="fa fa-minus" />Detach from feed
        </button>
        <button class="btn btn-ss-danger btn-icon-sm" type="button" @click="props.do({cb: deleteAllItems})">
          <i class="flaticon2-warning" />
          Delete
        </button>
      </template>
      <template #thumb="props">
        <img v-if="props.row.artwork" :src="props.row.artwork.thumbnail_path"
             alt="Thumb"
             class="thumbnail rounded"
             style="width: 64px;">
      </template>
      <template #description="props">
        <read-more :text="props.row.description"
                   more-str="[ Expand ]"
                   less-str="[ Hide ]"
                   :max-chars="80" />
      </template>
      <template #published="props">
        <span v-if="props.row.published === 1" style="color: green; font-weight: bold;">Yes</span>
        <span v-else>No</span>
      </template>
      <template #created_at="props">
        <span>{{ formatDateTime(props.row.created_at) }}</span>
      </template>
      <template #tags="props">
        <multi-select-tag-bag v-if="props.row.tags.length" :tags-list="props.row.tags.map(tag => tag.name)" />
        <span v-else class="no-content-notice">None</span>
      </template>
      <template #actions="props">
        <b-dropdown split
                    block
                    text="Edit"
                    :split-href="`/items/${props.row.uuid}/edit`">
          <b-dropdown-item-button v-if="ms_owned.val.param === `excluded_feed_uuid`" @click="() => props.do({ cb: assignToFeed })">
            <i class="flaticon2-plus" />Assign to feed
          </b-dropdown-item-button>
          <b-dropdown-item-button v-else @click="() => props.do({ cb: unassignFromFeed })">
            <i class="fa fa-minus" />Remove from feed
          </b-dropdown-item-button>
          <b-dropdown-item-button variant="ss-danger" @click="() => props.do({ cb: deleteAllItems })">
            <i class="flaticon2-warning" />Delete
          </b-dropdown-item-button>
        </b-dropdown>
      </template>
    </f-data-table>
    <delete-confirm-modal name="delete-confirm"
                          :entities-to-delete="selected_items"
                          entity-type="item" />
  </section>
</template>

<script>
import {
  BDropdown,
  BDropdownItemButton,
} from 'bootstrap-vue';

import { nullish } from 'Lib/type-sanity';
import FDataTable from "VuePartials/App/Storage/FDataTable/FDataTable.vue";
import FDataTableFilters from "VuePartials/App/Storage/FDataTable/FDataTableFilters.vue";
import FDataTableFilter from "VuePartials/App/Storage/FDataTable/FDataTableFilter";
import DeleteConfirmModal from 'VueModals/DeleteConfirmModal';
import ReadMore from 'VueComponents/ReadMoreComponent';
import MultiSelectTagBag from 'VueComponents/MultiSelectTagBag';

export default {
  components: {
    FDataTable,
    FDataTableFilter,
    FDataTableFilters,
    ReadMore,
    BDropdown,
    BDropdownItemButton,
    MultiSelectTagBag,
    DeleteConfirmModal,
  },
  props: {
    feed: {
      type: Object,
      required: true,
    },
    expanded: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    selected_items: [],
    data_uri: `/api/v1/items`,
    columns: [
      {
        field: `thumb`,
        display: `Thumbnail`,
        sortable: false,
        width: 100,
        clip_resistance: -1,
      },
      {
        field: `name`,
        display: `Name`,
        sort_dir: `asc`,
        clip_resistance: 1,
      },
      {
        field: `description`,
        display: `Description`,
      },
      {
        field: `published`,
        display: `Published`,
      },
      {
        field: `created_at`,
        display: `Created at`,
        active_sort: true,
      },
      {
        field: `tags`,
        display: `Tags`,
        sortable: false,
      },
      {
        field: `actions`,
        display: `Actions`,
        width: 150,
        sortable: false,
        never_clip: true,
      },
    ],
    filtering_owned: false,
    static_params: [
      {
        field: `feed_uuid`,
        value: window.feed.uuid,
      },
    ],
    ms_owned: {
      val: {
        display: `Contained in feed`,
        param: `feed_uuid`,
      },
      options: [
        { display: `Contained in feed`, param: `feed_uuid` },
        { display: `Not contained in feed`, param: `excluded_feed_uuid` },
      ],
    },
    published_multi_select_val: null,
    tags_input_val: '',
  }),
  asyncComputed: {
    tags_ac_list: {
      async get() {
        try {
          const res_data = (await axios.get('/api/v1/tags?type=item&per_page=10000'))?.data;
          return this.fallback(res_data?.data, []);
        } catch (err) {
          window.handleAxiosError(err);
          return [];
        }
      },
      default: [],
    },
  },
  mounted() {
    EventBus.$on(`fdt-rows-selected`, (ev) => {
      this.selected_items = ev;
    });
  },
  methods: {
    async toggleOwned(event, cb) {
      console.error(event);
      this.static_params = !nullish(event) ? [
        {
          field: event.param,
          value: this.feed.uuid,
        },
      ] : [];
      await new Promise((res) => {
        setTimeout(res, 100);
      });
      cb();
    },
    handleResponse(res, success_message, error_message = `Error`) {
      if (res.data && res.data.success) {
        toastr.success(success_message || res.data.message || `Success!`);
      } else {
        if (res.error?.message) {
          error_message = `${error_message}: ${res.error.message}`;
        }
        toastr.error(error_message || `Something went wrong!`);
      }
    },
    async doItemAJAXAction(ids, method, options) {
      const config = {
        absolute_path: false,
        id_key: `ids`,
        endpoint: ``,
        success_message: `Success!`,
        error_message: `Error`,
        ...options,
      };
      try {
        const res = await method(`${config.absolute_path ? `` : `/api/v1/feeds`}${config.endpoint}`, {
          [config.id_key]: ids,
        });

        window.EventBus.$emit('feeds_updated', [res.data]);

        this.handleResponse(res, config.success_message, config.error_message);
      } catch (err) {
        console.error(err);
        toastr.error(`Something went wrong`);
      }
    },
    assignToFeed(...items) {
      return this.doItemAJAXAction(items.map(item => item.uuid), axios.post, {
        id_key: `item_uuids`,
        endpoint: `/${this.feed.uuid}/attachItems`,
        success_message: `Successfully assigned ${items.length} ${this.item_label}(s) to feed ${this.feed.name}!`,
        error_message: `Error assigning ${this.item_label}(s) to feed.`,
      });
    },
    unassignFromFeed(...items) {
      return this.doItemAJAXAction(items.map(item => item.uuid), axios.post, {
        id_key: `item_uuids`,
        endpoint: `/${this.feed.uuid}/detachItems`,
        success_message: `Successfully detached ${items.length} ${this.item_label}(s) from feed ${this.feed.name}!`,
        error_message: `Error removing ${this.item_label}(s) from feed.`,
      });
    },
    async deleteAllItems(...items) {
      const res = await this.fireModal(`delete-confirm`);
      if (!res.data.confirm) {
        return false;
      }
      return this.doItemAJAXAction(null, axios.delete, {
        absolute_path: true,
        endpoint: `/api/v1/items/${items.map(item => item.uuid).join(`,`)}`,
        success_message: `Successfully deleted ${items.length} item(s)!`,
        error_message: `Error when attempting to delete`,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
i[class*='flaticon'] {
  &::before {
    margin-right: 0.4em;
  }
}

.ms-filter {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 30%;

  label {
    font-weight: bold;
  }
}
</style>
